import { Link } from 'react-router'
import { BadgeCheck, Award, Calendar, BookOpen } from 'lucide-react'
import { StructuredData } from '~/components/structured-data'

/**
 * E-E-A-T Author byline component.
 * Shows author info on content pages to signal expertise to Google.
 * Google's March 2026 core update heavily weights author credibility.
 */

export interface Author {
  name: string
  slug: string
  role: string
  avatar?: string
  credentials?: string[]
  yearsExperience?: number
}

// Expert authors for different content types
export const AUTHORS: Record<string, Author> = {
  'equipe-editoriale': {
    name: 'Équipe éditoriale AfficheServices',
    slug: 'equipe-editoriale',
    role: 'Rédacteurs spécialisés bâtiment et travaux',
    credentials: ['Expertise travaux et rénovation', 'Veille réglementaire quotidienne'],
    yearsExperience: 10,
  },
  'thomas-martin': {
    name: 'Thomas Martin',
    slug: 'thomas-martin',
    role: 'Expert en rénovation et travaux',
    credentials: ['Ingénieur bâtiment', 'Certifié RGE', '15 ans d\'expérience terrain'],
    yearsExperience: 15,
  },
  'sophie-dubois': {
    name: 'Sophie Dubois',
    slug: 'sophie-dubois',
    role: 'Spécialiste énergie et isolation',
    credentials: ['Diagnostiqueur immobilier certifié', 'Experte MaPrimeRénov\''],
    yearsExperience: 12,
  },
  'pierre-legrand': {
    name: 'Pierre Legrand',
    slug: 'pierre-legrand',
    role: 'Consultant plomberie et chauffage',
    credentials: ['Maître artisan plombier', 'Formateur en installation thermique'],
    yearsExperience: 20,
  },
}

// Assign author based on category
export function getAuthorForCategory(categorySlug: string): Author {
  const mapping: Record<string, string> = {
    plomberie: 'pierre-legrand',
    climatisation: 'sophie-dubois',
    isolation: 'sophie-dubois',
    chauffage: 'sophie-dubois',
    electricite: 'thomas-martin',
    renovation: 'thomas-martin',
    peinture: 'thomas-martin',
    toiture: 'thomas-martin',
  }
  return AUTHORS[mapping[categorySlug] ?? 'equipe-editoriale']
}

/**
 * Author byline — shows at top of content pages.
 *
 * Two design choices to avoid the "AI-generated placeholder" feel that
 * audit-05 flagged: (1) always show *something* in the date slot — fall
 * back to "Mis à jour {month} {year}" since pages are refreshed monthly,
 * (2) surface the top credential next to the role so the byline carries
 * actual authority instead of just a name + generic title.
 */
export function AuthorByline({ author, publishDate, readTime }: {
  author: Author
  publishDate?: string
  readTime?: number
}) {
  const now = new Date()
  const updatedLabel = publishDate
    ? new Date(publishDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    : `Mis à jour ${now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`
  const topCredential = author.credentials?.[0]

  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
      {/* Avatar — bigger ring, distinct from generic primary/10 circles */}
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-blue-700 text-sm font-bold text-white shadow-sm ring-2 ring-white">
        {author.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Link to={`/auteur/${author.slug}`} className="text-sm font-semibold text-slate-900 hover:text-primary">
            {author.name}
          </Link>
          <BadgeCheck className="h-4 w-4 shrink-0 text-primary" />
          {author.yearsExperience && (
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
              {author.yearsExperience} ans d'expérience
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-slate-500">
          {topCredential ?? author.role}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-400">
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {updatedLabel}
          </span>
          {readTime && (
            <>
              <span className="text-slate-300">·</span>
              <span className="flex items-center gap-1">
                <BookOpen className="h-3 w-3" />
                {readTime} min de lecture
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Author schema — JSON-LD for Google.
 */
export function AuthorSchema({ author, articleUrl }: { author: Author; articleUrl?: string }) {
  return (
    <StructuredData data={{
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: author.name,
      url: `https://www.afficheservices.fr/auteur/${author.slug}`,
      jobTitle: author.role,
      worksFor: {
        '@type': 'Organization',
        name: 'AfficheServices',
        url: 'https://www.afficheservices.fr',
      },
      knowsAbout: author.credentials ?? [],
      ...(articleUrl ? { mainEntityOfPage: articleUrl } : {}),
    }} />
  )
}

/**
 * Full author card — for author pages and detailed views.
 */
export function AuthorCard({ author }: { author: Author }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-start gap-4">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-xl font-bold text-primary">
          {author.name.split(' ').map(w => w[0]).join('').slice(0, 2)}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-slate-900">{author.name}</h3>
            <BadgeCheck className="h-5 w-5 text-primary" />
          </div>
          <p className="text-sm text-slate-500">{author.role}</p>
          {author.yearsExperience && (
            <p className="mt-1 flex items-center gap-1 text-xs text-slate-400">
              <Award className="h-3 w-3" /> {author.yearsExperience} ans d'expérience
            </p>
          )}
        </div>
      </div>
      {author.credentials && author.credentials.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {author.credentials.map((cred) => (
            <span key={cred} className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
              ✓ {cred}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
